// In-call panels (layer 2): the meeting's workspace files for everyone, and the host's
// per-guest switches. Rendered in the call's drawer slot, so Work mode can re-home them later.
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type Snapshot } from '../api';
import { ACCEPT, MAX_BYTES, formatSize, openVersion, uploadWithProgress, type FileEntry } from '../files';
import { baseName, contents, crumbs, joinPath } from '../tree';
import { t, T } from '../tenant';

interface FilesResponse {
  workspace: { id: string; title: string } | null;
  files: FileEntry[];
  folders: string[];
  canDownload: boolean;
  canUpload: boolean;
}

type ShareState = { name: string; phase: 'asking' | 'waiting' | 'uploading' | 'done' | 'denied' | 'error'; progress?: number; message?: string };

export function FilesPanel({ meetingId, host, onClose, onPresented }: { meetingId: string; host: boolean; onClose: () => void; onPresented?: () => void }) {
  const [data, setData] = useState<FilesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [share, setShare] = useState<ShareState | null>(null);
  const [folder, setFolder] = useState('');
  const picker = useRef<HTMLInputElement>(null);

  const load = useCallback(() => api<FilesResponse>(`/api/m/${meetingId}/files`, { meetingId }).then(setData, (e: Error) => setError(e.message)), [meetingId]);
  // Puts the file on the stage for everyone; PDFs and images only (the same files that open in the browser).
  const present = (versionId: string) =>
    api(`/api/m/${meetingId}/present`, { body: { versionId }, meetingId }).then(
      () => onPresented?.(),
      (e: Error) => setError(e.message),
    );
  useEffect(() => {
    void load();
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [load]);

  async function upload(file: File, path: string) {
    setShare({ name: file.name, phase: 'uploading', progress: 0 });
    await uploadWithProgress(path, file, (p) => setShare({ name: file.name, phase: 'uploading', progress: p }), meetingId);
    setShare({ name: file.name, phase: 'done' });
    void load();
  }

  async function shareFile(file: File) {
    setError(null);
    if (file.size > MAX_BYTES) return setShare({ name: file.name, phase: 'error', message: 'Larger than 95 MB.' });
    try {
      // The host's upload lands in the folder they are looking at; guest uploads go to the top level.
      if (host) return await upload(file, `/api/m/${meetingId}/upload?name=${encodeURIComponent(joinPath(here, file.name))}`);
      // Guests ask first; the browser keeps the file and uploads the moment the host approves.
      setShare({ name: file.name, phase: 'asking' });
      const r = await api<{ requestId: string; state: string }>(`/api/m/${meetingId}/upload-request`, { body: { name: file.name, size: file.size }, meetingId });
      let state = r.state;
      if (state === 'pending') setShare({ name: file.name, phase: 'waiting' });
      while (state === 'pending') {
        await new Promise((res) => setTimeout(res, 2000));
        state = (await api<{ state: string }>(`/api/m/${meetingId}/upload-request?req=${r.requestId}`, { meetingId })).state;
      }
      if (state !== 'approved') return setShare({ name: file.name, phase: 'denied' });
      await upload(file, `/api/m/${meetingId}/upload?req=${r.requestId}`);
    } catch (err) {
      setShare({ name: file.name, phase: 'error', message: (err as Error).message });
    }
  }

  // A folder that disappeared (moved or deleted by the host) falls back to the top level.
  const here = data && (folder === '' || data.folders.includes(folder)) ? folder : '';
  const view = data ? contents(data.files, data.folders, here) : { folders: [], files: [] };

  const shareText: Record<ShareState['phase'], string> = {
    asking: 'Asking the host…',
    waiting: 'Waiting for the host to approve…',
    uploading: 'Uploading',
    done: 'Shared.',
    denied: 'The host did not accept this file.',
    error: 'Not shared',
  };

  return (
    <div className="drawer" role="dialog" aria-label="Files" data-testid="files-panel">
      <div className="drawer-head">
        <strong style={{ flex: 1 }}>Files{data?.workspace ? ` · ${data.workspace.title}` : ''}</strong>
        <button className="btn ghost sm" onClick={onClose} aria-label="Close files">
          Close
        </button>
      </div>
      <div className="drawer-body">
        {error && <div className="error">{error}</div>}
        {data && !data.workspace && <div className="muted small">{host ? `This ${t.meeting} has no ${t.workspace}. Pick one when you create a ${t.meeting}.` : `No files in this ${t.meeting}.`}</div>}
        {data?.workspace && (
          <>
            <button className="btn primary" onClick={() => picker.current?.click()} data-testid="share-file">
              {host ? 'Upload a file' : 'Share a file'}
            </button>
            <input
              ref={picker}
              type="file"
              accept={ACCEPT}
              hidden
              data-testid="call-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void shareFile(f);
                e.target.value = '';
              }}
            />
            {!host && !data.canUpload && <div className="muted small">The host approves each file before it is shared.</div>}
          </>
        )}
        {share && (
          <div className={share.phase === 'error' || share.phase === 'denied' ? 'error' : 'notice'} data-testid="share-status">
            {share.name}: {shareText[share.phase]}
            {share.phase === 'uploading' && ` ${Math.round((share.progress ?? 0) * 100)}%`}
            {share.message ? ` — ${share.message}` : ''}
          </div>
        )}
        {data?.workspace && (
          <nav className="crumbs" aria-label="Folder" data-testid="call-crumbs">
            {crumbs(here, data.workspace.title).map((c, i) => (
              <span key={c.path} className="row" style={{ gap: 2 }}>
                {i > 0 && <span className="sep">/</span>}
                <button type="button" onClick={() => setFolder(c.path)}>
                  {c.label}
                </button>
              </span>
            ))}
          </nav>
        )}
        {view.folders.map((p) => (
          <div key={p} className="list-item folder-row" onClick={() => setFolder(p)} data-testid="call-folder">
            <span className="icon" aria-hidden>
              📁
            </span>
            <strong style={{ flex: 1 }}>{baseName(p)}</strong>
          </div>
        ))}
        {data?.workspace && view.folders.length === 0 && view.files.length === 0 && <div className="muted small">{here ? 'This folder is empty.' : 'No files yet.'}</div>}
        {view.files.map((f) =>
          f.current ? (
            <div key={f.id} className="list-item" data-testid="call-file">
              <div style={{ flex: 1, minWidth: 140 }}>
                <div>{baseName(f.name)}</div>
                <div className="muted small">
                  v{f.current.version} · {formatSize(f.current.size)}
                  {f.current.uploaded_by_kind === 'guest' ? ` · from guest ${f.current.uploaded_by_name}` : ''}
                </div>
              </div>
              {data?.canDownload ? (
                <div className="row">
                  {f.inline && (
                    <button className="btn primary sm" onClick={() => void present(f.current!.id)} data-testid="present-file">
                      Present
                    </button>
                  )}
                  {f.inline && (
                    <button className="btn sm" onClick={() => openVersion(`/api/m/${meetingId}/files/${f.current!.id}`, false, meetingId).catch((e: Error) => setError(e.message))}>
                      Open
                    </button>
                  )}
                  <button className="btn sm" onClick={() => openVersion(`/api/m/${meetingId}/files/${f.current!.id}`, true, meetingId).catch((e: Error) => setError(e.message))}>
                    Download
                  </button>
                </div>
              ) : (
                <span className="muted small">Download off</span>
              )}
            </div>
          ) : null,
        )}
      </div>
    </div>
  );
}

const ISSUE_TEXT: Record<string, string> = {
  NotAllowedError: 'Blocked by the browser or phone settings',
  NotFoundError: 'No device found',
  NotReadableError: 'In use by another app or call',
  AbortError: 'Interrupted by the phone',
  OverconstrainedError: 'Chosen device unavailable',
  SecurityError: 'Browser does not allow it here',
};

export function GuestsPanel({ meetingId, snapshot, onChange, onClose }: { meetingId: string; snapshot: Snapshot | null; onChange: () => void; onClose: () => void }) {
  // Optimistic switches: show the new value at once; the next snapshot confirms it.
  const [overrides, setOverrides] = useState<Record<string, { canDownload?: boolean; uploadAll?: boolean }>>({});
  useEffect(() => setOverrides({}), [snapshot]);
  // In the call first; anyone who left is marked, not a second live entry.
  const guests = (snapshot?.guests.filter((g) => g.state === 'admitted') ?? []).map((g) => ({ ...g, ...overrides[g.id] })).sort((a, b) => Number(b.present !== false) - Number(a.present !== false));
  const set = (guestId: string, perms: { canDownload?: boolean; uploadAll?: boolean }) => {
    setOverrides((o) => ({ ...o, [guestId]: { ...o[guestId], ...perms } }));
    return api(`/api/m/${meetingId}/guest-perms`, { body: { guestId, ...perms } }).then(onChange, onChange);
  };
  return (
    <div className="drawer" role="dialog" aria-label="Guests" data-testid="guests-panel">
      <div className="drawer-head">
        <strong style={{ flex: 1 }}>{T.guests} in this {t.meeting}</strong>
        <button className="btn ghost sm" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="drawer-body">
        {guests.length === 0 && <div className="muted small">No admitted guests yet.</div>}
        {guests.some((g) => g.present !== false) && (
          <button className="btn sm" onClick={() => void api(`/api/m/${meetingId}/mute`, { body: { guestId: 'all' } }).then(onChange, onChange)} data-testid="mute-all">
            Mute everyone
          </button>
        )}
        {guests.map((g) => (
          <div key={g.id} className="list-item" data-testid="guest-row" style={g.present === false ? { opacity: 0.6 } : undefined}>
            <div style={{ flex: 1, minWidth: 120 }}>
              {g.name}
              {g.present === false && (
                <span className="tag" style={{ marginLeft: 6 }} data-testid="guest-left">
                  left the call
                </span>
              )}
              {g.issue && (
                <div className="small" style={{ color: 'var(--color-status-danger)' }} data-testid="guest-issue">
                  {ISSUE_TEXT[g.issue.name] ?? 'Device problem'}: {g.issue.source === 'camera' ? 'camera' : 'microphone'}
                </div>
              )}
            </div>
            <label className="row small">
              <input type="checkbox" checked={g.canDownload} onChange={(e) => void set(g.id, { canDownload: e.target.checked })} data-testid="guest-download" /> Download
            </label>
            <label className="row small" title="Skip approval for this guest's uploads for the rest of the meeting">
              <input type="checkbox" checked={g.uploadAll} onChange={(e) => void set(g.id, { uploadAll: e.target.checked })} /> Upload without asking
            </label>
            {g.present !== false && (
              <button className="btn sm" onClick={() => void api(`/api/m/${meetingId}/mute`, { body: { guestId: g.id } }).then(onChange, onChange)} data-testid="guest-mute">
                Mute
              </button>
            )}
            <button className="btn ghost sm" onClick={() => api(`/api/m/${meetingId}/deny`, { body: { guestId: g.id } }).then(onChange)}>
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Host toasts for pending guest uploads. */
export function UploadRequestToasts({ meetingId, snapshot, onChange }: { meetingId: string; snapshot: Snapshot | null; onChange: () => void }) {
  const decide = (requestId: string, decision: 'file' | 'meeting' | 'deny') => api(`/api/m/${meetingId}/upload-decision`, { body: { requestId, decision } }).then(onChange, onChange);
  return (
    <>
      {snapshot?.uploads.map((u) => (
        <div key={u.id} className="toast" data-testid="upload-request">
          <strong>
            {u.guestName} wants to share a file
          </strong>
          <span className="muted small">
            {u.name} · {formatSize(u.size)}
          </span>
          <div className="row">
            <button className="btn primary sm" onClick={() => decide(u.id, 'file')}>
              Allow this file
            </button>
            <button className="btn sm" onClick={() => decide(u.id, 'meeting')}>
              Allow for meeting
            </button>
            <button className="btn ghost sm" onClick={() => decide(u.id, 'deny')}>
              Deny
            </button>
          </div>
        </div>
      ))}
    </>
  );
}
